import { UserIdentityConflictDefinition } from '~src/authentication/contentDefinitions/UserIdentityConflictDefinition';
import { ConnectedApplicationDefinition } from '~src/connectedApplications/content/ConnectedApplicationDefinition';
import { setCollectionSpaceContentInOrder, setContentSpaceDefinitions,setExplorableItemDefinitions, setFavoriteMixedContentInOrder, setMixedContentInOrder, setPersonalSpaceContentInOrder } from '~src/contentDefinitionInterfaces/ContentDefinitionsLists';
import { ExplorableItemDefinition, MixedContentItemDefinition, SpaceDefinition } from '~src/contentDefinitionInterfaces/ContentDefinitionsTypes';
import { MetricDefinition } from '~src/metrics/models/MetricDefinition';
import {
  ExplorableItemType,
  SpaceInfoItemType,
} from '~src/spacesPlatform/models/ContentType';

import { AccelerationTaskDefinition } from '../dataAcceleration/contentDefinitions/AccelerationTaskDefinition';
import { WorkbookAccelerationDefinition } from '../dataAcceleration/contentDefinitions/WorkbookAccelerationDefinition';
import { DataRoleDefinition } from '../dataRole/contentDefinitions/DataRoleDefinition';
import { DatasourceDefinition } from '../datasource/components/contentDefinitions/DatasourceDefinition';
import { DataAlertDefinition } from '../dda/contentDefinitions/DataAlertDefinition';
import { RemoteRefreshSchedulesDefinition } from '../extractRefresh/contentDefinitions/RemoteRefreshSchedulesDefinition';
import { FlowDefinition } from '../flow/contentDefinitions/FlowDefinition';
import { FlowOutputStepRunDefinition } from '../flow/contentDefinitions/FlowOutputStepRunDefinition';
import { RunFlowTaskDefinition } from '../flow/contentDefinitions/RunFlowTaskDefinition';
import { LensDefinition } from '../lens/contentDefinitions/LensDefinition';
import { PredictiveModelDefinition } from '../predictiveModel/contentDefinition/PredictiveModelDefinition';
import { PublishedConnectionColumnDefinition } from '../publishedConnection/contentDefinitions/PublishedConnectionColumnDefinition';
import { PublishedConnectionDefinition } from '../publishedConnection/contentDefinitions/PublishedConnectionDefinition';
import { PublishedConnectionTableDefinition } from '../publishedConnectionTable/contentDefinitions/PublishedConnectionTableDefinition';
import { SiteDefinition } from '../siteManagement/contentDefinitions/SiteDefinition';
import { SubscriptionDefinition } from '../subscription/contentDefinitions/SubscriptionDefinition';
import { FeatureFlags } from '../ts/FeatureFlags';
import { ServerUserDefinition } from '../userManagement/users/contentDefinition/ServerUserDefinition';
import { SiteUserDefinition } from '../userManagement/users/contentDefinition/SiteUserDefinition';
import { CustomViewDefinition } from '../views/contentDefinitions/customViews/CustomViewDefinition';
import { WorkbookDefinition } from '../workbook/components/contentDefinitions/WorkbookDefinition';
import { BackgroundJobDefinition } from './BackgroundJobDefinition';
import { CollectionDefinition } from './CollectionDefinition';
import { ConnectedWorkbooksInDatasourceDefinition } from './ConnectedWorkbooksInDatasourceDefinition';
import { DatabaseDefinition, DatabaseWithCrassDefinition } from './DatabaseDefinition';
import { DataConnectionDefinition } from './DataConnectionDefinition';
import { DataConnectionInDatasourceDefinition } from './DataConnectionInDatasourceDefinition';
import { ExtractTaskDefinition } from './ExtractTaskDefinition';
import { GroupDefinition } from './GroupDefinition';
import { ProjectDefinition } from './ProjectDefinition';
import { RecommendedViewDefinition } from './RecommendedViewDefinition';
import { ResourceGovernanceSettingDefiniton } from './ResourceGovernanceSettingDefinition';
import { ScheduleDefinition } from './ScheduleDefinition';
import { SearchUserDefinition } from './SearchUserDefinition';
import { TableDefinition, TableWithCrassDefinition } from './TableDefinition';
import { ViewDefinition } from './ViewDefinition';

const mixedContentInOrder: MixedContentItemDefinition[] = [
  ProjectDefinition,
  WorkbookDefinition,
  ViewDefinition,
  MetricDefinition,
  LensDefinition,
  DatasourceDefinition,
  FlowDefinition,
  DataRoleDefinition,
  PublishedConnectionDefinition,
  PredictiveModelDefinition,
];

const collectionSpaceContentInOrder: MixedContentItemDefinition[] = [
  ProjectDefinition,
  WorkbookDefinition,
  ViewDefinition,
  MetricDefinition,
  LensDefinition,
  DatasourceDefinition,
  FlowDefinition,
  DataRoleDefinition,
  PublishedConnectionDefinition,
  PublishedConnectionTableDefinition,
  PredictiveModelDefinition,
];

const personalSpaceContentInOrder: MixedContentItemDefinition[] = [
  WorkbookDefinition,
  DatasourceDefinition,
];

const favoriteMixedContentInOrder: MixedContentItemDefinition[] = [
  ProjectDefinition,
  WorkbookDefinition,
  ViewDefinition,
  MetricDefinition,
  DatasourceDefinition,
  FlowDefinition,
  DataRoleDefinition,
  CollectionDefinition,
  PublishedConnectionDefinition,
  PublishedConnectionTableDefinition
];

// TFS 1283291: add Lenses to Favorites Dropdown if FeatureFlag is on
const favoriteMixedContentInOrderWithLens: MixedContentItemDefinition[] = [
    ProjectDefinition,
    WorkbookDefinition,
    ViewDefinition,
    MetricDefinition,
    DatasourceDefinition,
    FlowDefinition,
    DataRoleDefinition,
    CollectionDefinition,
    PublishedConnectionDefinition,
    PublishedConnectionTableDefinition,
    LensDefinition,
];

// TFS 1283291: add Lenses to Favorites Dropdown if FeatureFlag is on
function getProperFavoriteMixedContentInOrder(): MixedContentItemDefinition[] {
  return FeatureFlags.askDataLensFavoritingEnabled ? favoriteMixedContentInOrderWithLens : favoriteMixedContentInOrder;
}

const explorableItemDefinitions: Record<
  ExplorableItemType,
  ExplorableItemDefinition
> = {
  accelerationTask: AccelerationTaskDefinition,
  backgroundJob: BackgroundJobDefinition,
  collection: CollectionDefinition,
  connectedApplication: ConnectedApplicationDefinition,
  connectedWorkbook: ConnectedWorkbooksInDatasourceDefinition,
  connection: DataConnectionInDatasourceDefinition,
  customView: CustomViewDefinition,
  dataalert: DataAlertDefinition,
  database: DatabaseDefinition,
  databaseWithCrass: DatabaseWithCrassDefinition,
  dataconnection: DataConnectionDefinition,
  dataRole: DataRoleDefinition,
  datasource: DatasourceDefinition,
  extractTask: ExtractTaskDefinition,
  flow: FlowDefinition,
  flowOutputStepRun: FlowOutputStepRunDefinition,
  group: GroupDefinition,
  lens: LensDefinition,
  metric: MetricDefinition,
  predictiveModel: PredictiveModelDefinition,
  project: ProjectDefinition,
  virtualConnection: PublishedConnectionDefinition,
  virtualConnectionTable: PublishedConnectionTableDefinition,
  virtualConnectionColumn: PublishedConnectionColumnDefinition,
  recommendedView: RecommendedViewDefinition,
  remoteRefreshSchedule: RemoteRefreshSchedulesDefinition,
  resourceGovernanceSetting: ResourceGovernanceSettingDefiniton,
  runFlowTask: RunFlowTaskDefinition,
  schedule: ScheduleDefinition,
  searchuser: SearchUserDefinition,
  serveruser: ServerUserDefinition,
  userIdentityConflict: UserIdentityConflictDefinition,
  site: SiteDefinition,
  siteuser: SiteUserDefinition,
  subscription: SubscriptionDefinition,
  table: TableDefinition,
  tableWithCrass: TableWithCrassDefinition,
  view: ViewDefinition,
  workbook: WorkbookDefinition,
  workbookAcceleration: WorkbookAccelerationDefinition,
};

const contentSpaceDefinitions: {
  [C in SpaceInfoItemType]: SpaceDefinition<C>;
} = {
  collection: CollectionDefinition,
  connectedApplication: ConnectedApplicationDefinition,
  dataRole: DataRoleDefinition,
  datasource: DatasourceDefinition,
  flow: FlowDefinition,
  group: GroupDefinition,
  lens: LensDefinition,
  metric: MetricDefinition,
  predictiveModel: PredictiveModelDefinition,
  project: ProjectDefinition,
  virtualConnection: PublishedConnectionDefinition,
  virtualConnectionTable: PublishedConnectionTableDefinition,
  resourceGovernanceSetting: ResourceGovernanceSettingDefiniton,
  schedule: ScheduleDefinition,
  serveruser: ServerUserDefinition,
  siteuser: SiteUserDefinition,
  view: ViewDefinition,
  workbook: WorkbookDefinition,
};

setMixedContentInOrder(mixedContentInOrder);
setCollectionSpaceContentInOrder(collectionSpaceContentInOrder);
setPersonalSpaceContentInOrder(personalSpaceContentInOrder);
setFavoriteMixedContentInOrder(getProperFavoriteMixedContentInOrder());
setExplorableItemDefinitions(explorableItemDefinitions);
setContentSpaceDefinitions(contentSpaceDefinitions);
